import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { submitContactForm } from '@/lib/api';

const contactSchema = z.object({
  name: z.string().min(2, 'Please enter your name'),
  email: z.string().email('Please enter a valid email address'),
  phone: z.string().optional(),
  company: z.string().optional(),
  message: z.string().min(10, 'Please enter at least 10 characters'),
});

type ContactFormValues = z.infer<typeof contactSchema>;

export default function ContactForm() {
  const [submitState, setSubmitState] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ContactFormValues>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      company: '',
      message: '',
    },
  });

  const onSubmit = async (values: ContactFormValues) => {
    setSubmitState('idle');
    setErrorMessage('');

    try {
      await submitContactForm({
        name: values.name,
        email: values.email,
        phone: values.phone || undefined,
        company: values.company || undefined,
        message: values.message,
      });
      setSubmitState('success');
      reset();
    } catch (error) {
      setSubmitState('error');
      setErrorMessage(
        error instanceof Error
          ? error.message
          : 'Something went wrong. Please try again later.'
      );
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <div className="grid gap-6 sm:grid-cols-2">
        <div>
          <label htmlFor="name" className="block text-base font-bold text-slate-700">
            Name <span className="text-red-500">*</span>
          </label>
          <input id="name" type="text" className="input-field mt-1" {...register('name')} />
          {errors.name && (
            <p className="mt-1 text-base font-semibold text-red-600">{errors.name.message}</p>
          )}
        </div>

        <div>
          <label htmlFor="email" className="block text-base font-bold text-slate-700">
            Email <span className="text-red-500">*</span>
          </label>
          <input id="email" type="email" className="input-field mt-1" {...register('email')} />
          {errors.email && (
            <p className="mt-1 text-base font-semibold text-red-600">{errors.email.message}</p>
          )}
        </div>

        <div>
          <label htmlFor="phone" className="block text-base font-bold text-slate-700">
            Phone
          </label>
          <input id="phone" type="tel" className="input-field mt-1" {...register('phone')} />
        </div>

        <div>
          <label htmlFor="company" className="block text-base font-bold text-slate-700">
            Company
          </label>
          <input id="company" type="text" className="input-field mt-1" {...register('company')} />
        </div>
      </div>

      <div>
        <label htmlFor="message" className="block text-base font-bold text-slate-700">
          Message <span className="text-red-500">*</span>
        </label>
        <textarea
          id="message"
          rows={5}
          className="input-field mt-1"
          {...register('message')}
        />
        {errors.message && (
          <p className="mt-1 text-base font-semibold text-red-600">{errors.message.message}</p>
        )}
      </div>

      {submitState === 'success' && (
        <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-base font-semibold text-green-800">
          Thank you! Your message has been sent. We&apos;ll get back to you soon.
        </div>
      )}

      {submitState === 'error' && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-base font-semibold text-red-800">
          {errorMessage || 'Failed to send your message. Please try again.'}
        </div>
      )}

      <button type="submit" className="btn-primary sm:w-auto" disabled={isSubmitting}>
        {isSubmitting ? 'Sending...' : 'Send message'}
      </button>
    </form>
  );
}
